import React, { useState, useEffect } from "react";
import { Formik } from "formik";
import { useLocation, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import Breadcrums from "../../common/breadcrumbs";
import * as subscriptionServices from "../../services/subscription.services";
import { SWAL_SETTINGS, INPUT_LENGTH_500 } from "../../../../utils/Constants";
import {
  globalLoader,
  handleServerValidations,
} from "../../../../utils/commonfunction";
import { Tooltip, Whisper } from "rsuite";
import { useTranslation } from "react-i18next";
import CustomCkeditor from "../../common/customeditor";
import CategorySelect from "../../common/categorySelect";



const SubscriptionAdd = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { t } = useTranslation()
  const [submitted, setSubmitted] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  const formData = new FormData();
  const emptyAccessRow = { slug: "", label: "", value: "" };

  const createAccessSlug = (label = "", fallback = "access") =>
    label
      .toString()
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "") || fallback;

  const toAccessRow = (access) => {
    if (access && typeof access === "object" && !Array.isArray(access)) {
      return {
        slug: access.slug || access.key || "",
        label: access.label || "",
        value: access.value || "",
      };
    }

    if (typeof access === "string" && access.trim()) {
      const [left, ...rest] = access.split(":");
      if (rest.length > 0) {
        return { slug: createAccessSlug(left.trim()), label: left.trim(), value: rest.join(":").trim() };
      }
      return { slug: createAccessSlug(access.trim()), label: access.trim(), value: "" };
    }

    return { ...emptyAccessRow };
  };

  const [initialValues, setInitialValues] = useState({
    title: "",
    title_ja: "",
    name: "",
    price: "",
    duration_type: "",
    description: "",
    short_description_ja: "",
    meta_tags: "",
    meta_keyword: "",
    richtext: "",
    title_de: "",
    richtext_ja: "",
    subscription_access: [{ ...emptyAccessRow }],
    limit_type: "total",
  });



  const breadcrumbs = [
    { title: t("link_dashboard"), url: "/admin/dashboard/1" },
    { title: t("link_subscription"), url: "/admin/subscriptionManagement/list/1" },
    { title: t("link_add"), url: "" },
  ];

  const durationTypeOptions = [
    { _id: "month", title: t("month") },
    { _id: "year", title: t("year") },
  ];

  useEffect(() => {
    setInitialValues({
      title: state && state?.title ? state?.title : "",
      price: state && state?.price ? state?.price : "",
      duration_type: state && state?.duration_type ? state?.duration_type : "",
      title_ja: state && state?.title_ja ? state?.title_ja : "",
      status: state && state?.status ? state.status : "",
      menu_location: state && state?.menu_location ? state.menu_location : "",
      description: state && state?.description ? state?.description : "",
      short_description_ja: state && state?.short_description_ja ? state?.short_description_ja : "",
      richtext: state && state?.description ? state?.description : "",
      richtext_ja: state && state?.richtext_ja ? state?.richtext_ja : "",
      benefits: state && state?.benefits ? (Array.isArray(state.benefits) && state.benefits.length > 0 ? state.benefits : [""]) : [""],
      subscription_access:
        state && Array.isArray(state?.subscription_access) && state.subscription_access.length > 0
          ? state.subscription_access.map(toAccessRow)
          : [{ ...emptyAccessRow }],
      limit_type: state && state?.limit_type ? state?.limit_type : "total",
    });
    setDataLoaded(true);
  }, []);

  return (
    <>
      <Breadcrums data={breadcrumbs} />
      {dataLoaded ? (
        <Formik
          initialValues={initialValues}
          validateOnBlur={false}
          validate={(values) => {
            const error = {};
            if (!values.title || !values.title.trim()) error.title = "label_title_error";
            if (!values.price || !values.price) error.price = "label_price_error";
            if (!values.duration_type || !values.duration_type) error.duration_type = "label_duration_type_error";
            if (values.title.length > INPUT_LENGTH_500) error.title = "validation_err_reached_maximum_length";
            if (!values.description || !values.description.trim()) error.description = "label_description_error";
            const hasSubscriptionAccess = (values.subscription_access || []).some(
              (access) => (access?.label || "").trim() !== "" || (access?.value || "").trim() !== ""
            );
            if (!hasSubscriptionAccess) error.subscription_access = "label_subscription_access_error";
            const hasBenefits = (values.benefits || []).some((benefit) => (benefit || "").trim() !== "");
            if (!hasBenefits) error.benefits = "label_benefits_error";

            return error;
          }}
          onSubmit={(values, { setSubmitting, resetForm }) => {
            setSubmitted(true);
            globalLoader(true);
            console.log(values);
            formData.append("title", values.title);
            formData.append("price", values.price);
            formData.append("duration_type", values.duration_type);
            formData.append("description", values.description);
            formData.append(
              "subscription_access",
              JSON.stringify(
                values.subscription_access
                  .filter((access) => (access?.label || "").trim() !== "" || (access?.value || "").trim() !== "")
                  .map((access) => ({
                    slug: (access?.slug || access?.key || createAccessSlug(access?.label || "")).trim(),
                    label: (access?.label || "").trim(),
                    value: (access?.value || "").trim(),
                  }))
              )
            );
            formData.append("limit_type", values.limit_type);
            formData.append("benefits", JSON.stringify(values.benefits.filter(b => b.trim() !== "")));
            subscriptionServices
              .Add(formData)
              .then((response) => {
                setSubmitting(false);
                if (response.success) {
                  Swal.fire({
                    icon: "success",
                    text: response.message,
                    ...SWAL_SETTINGS,
                  });
                  setTimeout(() => {
                    resetForm({ values: "" });
                    globalLoader(false);
                    navigate(`/admin/subscriptionManagement/list/1`);
                  }, 1000);
                } else {
                  Swal.fire({
                    icon: "error",
                    text: handleServerValidations(response),
                    ...SWAL_SETTINGS,
                  });
                  setSubmitted(false);
                  globalLoader(false);
                }
              })
              .catch((error) => {
                console.log("error ====> ", error);
              });
          }}>
          {({ values, errors, touched, handleChange, handleBlur, handleSubmit, setFieldValue, setFieldTouched, isSubmitting }) => (
            <form onSubmit={handleSubmit}>
              <div className="row row-sm">
                <div className="col-lg-12 col-md-12 animation_fade">
                  <div className="card custom-card">
                    <div className="card-body">
                      <div>
                        <h6 className="main-content-label mb-3">{t("add_subscription")}</h6>
                      </div>
                      <div className="row row-sm">
                        <div className="col-lg-12 text-center form-group">
                          <label htmlFor="title" className="text-left d-flex">
                            {t("list_heading_title")}:<span className="requirestar">*</span>
                            {/* <Whisper
                              placement="top"
                              controlId="control-id-hover"
                              trigger="hover"
                              speaker={
                                <Tooltip>
                                  {t("admin.pages.tooltip_page_title")}
                                </Tooltip>
                              }
                            >
                              <span className="field-more-info mt-1 ms-1 cp">
                                ?
                              </span>
                            </Whisper> */}
                          </label>
                          <input name="title" id="title" type="text" onChange={handleChange} onBlur={handleBlur} value={values.title} className="form-control" placeholder={t("subscription_title_placeholder")} />
                          <span className="text-danger d-flex text-left">{errors.title && touched.title && t(errors.title)}</span>
                        </div>
                        <div className="col-lg-12 text-center form-group">
                          <label htmlFor="price" className="text-left d-flex">
                            {t("list_heading_price")}:<span className="requirestar">*</span>
                          </label>
                          <input name="price" id="price" type="Number" onChange={handleChange} onBlur={handleBlur} value={values.price} className="form-control" placeholder={t("subscription_price_placeholder")} />
                          <span className="text-danger d-flex text-left">{errors.price && touched.price && t(errors.price)}</span>
                        </div>
                        <div className="col-lg-12 text-center form-group">
                          <label className="text-left d-flex">
                            {t("subscription_access")}:
                          </label>
                          {values.subscription_access && values.subscription_access.length > 0
                            ? values.subscription_access.map((access, index) => (
                              <div key={index} className="d-flex mb-2 align-items-center gap-2">
                                <input
                                  name={`subscription_access[${index}].slug`}
                                  type="text"
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  value={values.subscription_access[index]?.slug || ""}
                                  className="form-control"
                                  placeholder={t("subscription_access_slug_placeholder")}
                                />
                                <input
                                  name={`subscription_access[${index}].label`}
                                  type="text"
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  value={values.subscription_access[index]?.label || ""}
                                  className="form-control"
                                  placeholder={t("subscription_access_label_placeholder")}
                                />
                                <input
                                  name={`subscription_access[${index}].value`}
                                  type="text"
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  value={values.subscription_access[index]?.value || ""}
                                  className="form-control"
                                  placeholder={t("subscription_access_value_placeholder")}
                                />
                                <button
                                  type="button"
                                  className="btn btn-danger"
                                  onClick={() => {
                                    const newAccess = [...(values.subscription_access || [])];
                                    newAccess.splice(index, 1);
                                    if (newAccess.length === 0) newAccess.push({ ...emptyAccessRow });
                                    setFieldValue("subscription_access", newAccess);
                                  }}
                                  style={{ marginLeft: "10px" }}
                                >
                                  <i className="ace-icon fa fa-minus"></i>
                                </button>
                              </div>
                            ))
                            : null}
                          <div className="text-left mt-2">
                            <button
                              type="button"
                              className="btn btn-info btn-sm"
                              onClick={() => {
                                setFieldValue("subscription_access", [...(values.subscription_access || []), { ...emptyAccessRow }]);
                              }}
                            >
                              <i className="ace-icon fa fa-plus mx-1"></i> Add More
                            </button>
                          </div>
                          <span className="text-danger d-flex text-left">{errors.subscription_access && t(errors.subscription_access)}</span>
                        </div>

                        <div className="col-lg-12 text-center form-group">
                          <label htmlFor="duration_type" className="text-left d-flex">
                            {t("list_heading_duration_type")}:<span className="requirestar">*</span>
                          </label>

                          <CategorySelect
                            data={durationTypeOptions}
                            value={values.duration_type}
                            onChange={(value) => {
                              setFieldValue("duration_type", value);
                              setFieldTouched("duration_type", true);
                            }}
                          />

                          <span className="text-danger d-flex text-left">{errors.duration_type && touched.duration_type && t(errors.duration_type)}</span>
                        </div>

                        {/* <div className="col-lg-12 text-center form-group">
                          <label htmlFor="duration_value" className="text-left d-flex">
                            {t("list_heading_duration_value")}:<span className="requirestar">*</span>
                          </label>
                          <input name="duration_value" id="duration_value" type="Number" onChange={handleChange} onBlur={handleBlur} value={values.duration_value} className="form-control" placeholder={t("subscription_duration_value_placeholder")} />
                          <span className="text-danger d-flex text-left">{errors.duration_value && touched.duration_value && t(errors.duration_value)}</span>
                        </div> */}
                        <div className="col-lg-12 text-center form-group">
                          <label htmlFor="description" className="text-left d-flex">
                            {t("label_description")}:<span className="requirestar">*</span>
                          </label>
                          <textarea name="description" type="text" onChange={handleChange} onBlur={handleBlur} value={values.description} className="form-control" placeholder={t("faq_description_placeholder")} rows="5" />
                          <span className="text-danger d-flex text-left">{errors.description && touched.description && t(errors.description)}</span>
                        </div>
                        <div className="col-lg-12 text-center form-group">
                          <label className="text-left d-flex">
                            {t("label_benefits")}:
                          </label>
                          {values.benefits && values.benefits.length > 0 ? values.benefits.map((benefit, index) => (
                            <div key={index} className="d-flex mb-2">
                              <input
                                name={`benefits[${index}]`}
                                type="text"
                                onChange={handleChange}
                                onBlur={handleBlur}
                                value={values.benefits[index]}
                                className="form-control"
                                placeholder={t("benefits_placeholder")}
                              />
                              {index >= 0 && (
                                <button
                                  type="button"
                                  className="btn btn-danger"
                                  onClick={() => {
                                    const newBenefits = [...values.benefits];
                                    newBenefits.splice(index, 1);
                                    if (newBenefits.length === 0) newBenefits.push("");
                                    setFieldValue("benefits", newBenefits);
                                  }}
                                  style={{ marginLeft: "10px" }}
                                >
                                  <i className="ace-icon fa fa-minus"></i>
                                </button>
                              )}
                            </div>
                          )) : null}
                          <div className="text-left mt-2">
                            <button
                              type="button"
                              className="btn btn-info btn-sm"
                              onClick={() => {
                                setFieldValue("benefits", [...(values.benefits || []), ""]);
                              }}
                            >
                              <i className="ace-icon fa fa-plus mx-1"></i> Add More
                            </button>
                          </div>
                          <span className="text-danger d-flex text-left">{errors.benefits && t(errors.benefits)}</span>
                        </div>
                        <div className="">
                          <button className="btn btn-main-primary signbtn mr-2" type="submit" disabled={submitted ? true : null}>
                            <i className="ace-icon fa fa-check bigger-110 mx-1"></i>
                            {t("btn_submit")}
                          </button>
                          <button className="btn ripple btn-secondary" type="button" disabled={submitted ? true : null} onClick={() => navigate(-1)}>
                            <i className="ace-icon fa fa-times bigger-110 mx-1"></i>
                            {t("btn_cancel")}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </form>
          )}
        </Formik>
      ) : (
        <></>
      )}
    </>
  );
};

export default SubscriptionAdd;
